import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Plus, Minus, Leaf, Flame } from 'lucide-react';

export default function MenuItemCard({ item }) {
    const [quantity, setQuantity] = useState(1);
    const [adding, setAdding] = useState(false);
    const [message, setMessage] = useState('');
    const { addToCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();

    const handleAddToCart = async () => {
        setAdding(true);
        setMessage('');

        try {
            await addToCart(item.id, quantity, '', item.restaurant_id, item);
            setMessage('✓ Added to cart!');
            setQuantity(1);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Add to cart error:', error);
            const errorMsg = error.response?.data?.message || 'Failed to add';
            setMessage('✗ ' + errorMsg);
            setTimeout(() => setMessage(''), 3000);
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="card flex flex-col">
            <div className="h-48 relative overflow-hidden">
                {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className={`w-full h-full object-cover ${item.stock_quantity <= 0 ? 'grayscale opacity-70' : ''}`} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No Image
                    </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                    {item.is_vegetarian && (
                        <span className="bg-green-100 text-green-800 p-1 rounded-full shadow-sm" title="Vegetarian">
                            <Leaf className="w-4 h-4" />
                        </span>
                    )}
                    {item.is_spicy && (
                        <span className="bg-red-100 text-red-800 p-1 rounded-full shadow-sm" title="Spicy">
                            <Flame className="w-4 h-4" />
                        </span>
                    )}
                </div>
                {item.stock_quantity <= 0 && (
                    <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
                        <span className="bg-red-600 text-white font-bold px-4 py-2 rounded shadow-lg transform -rotate-12">
                            Out of Stock
                        </span>
                    </div>
                )}
            </div>

            <div className="p-4 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                    <span className="text-lg font-bold text-primary-600">₹{item.price}</span>
                </div>

                <p className="text-gray-600 text-sm mb-4 flex-1">{item.description}</p>

                {/* Show success/error message */}
                {message && (
                    <div className={`text-sm mb-2 p-2 rounded ${message.includes('✓') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {message}
                    </div>
                )}

                <div className="flex items-center justify-between mt-auto">
                    <>
                        {item.stock_quantity > 0 && item.stock_quantity <= 5 && (
                            <span className="text-xs font-semibold text-orange-600 mr-2">
                                Only {item.stock_quantity} left!
                            </span>
                        )}
                        <div className={`flex items-center space-x-2 ${item.stock_quantity <= 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                            <button
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                className="p-1 rounded-full hover:bg-gray-100"
                                disabled={adding || item.stock_quantity <= 0}
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="font-medium w-6 text-center">{quantity}</span>
                            <button
                                onClick={() => {
                                    if (quantity >= item.stock_quantity) {
                                        setMessage(`✗ Only ${item.stock_quantity} available in stock!`);
                                        setTimeout(() => setMessage(''), 3000);
                                    } else {
                                        setQuantity(quantity + 1);
                                    }
                                }}
                                className="p-1 rounded-full hover:bg-gray-100"
                                disabled={adding || item.stock_quantity <= 0}
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>

                        <button
                            onClick={handleAddToCart}
                            disabled={adding || item.stock_quantity <= 0}
                            className={`btn-primary flex items-center space-x-1 ${item.stock_quantity <= 0 ? 'opacity-50 cursor-not-allowed bg-gray-400' : ''}`}
                        >
                            <Plus className="w-4 h-4" />
                            <span>{item.stock_quantity <= 0 ? 'Sold Out' : (adding ? 'Adding...' : 'Add')}</span>
                        </button>
                    </>
                </div>
            </div>
        </div>
    );
}